import type { COUNTRY_SHORTS } from '~/constants'
import type { Nullable } from '~/types'
import type { FinancialCommon } from '~/types/financial'

/**
 * Returns a collection of basic financial details, advanced properties, and
 * helpers.
 *
 * @param financial The financial details to get.
 * @returns The financial details.
 */
export function getFinancial(financial?: Nullable<FinancialCommon>) {
  // Base properties.
  const financialAddOns = financial?.AddOns ?? []
  const financialAmountDue = financial?.AmountDue ?? 0
  const financialBookingId = financial?.BookingId
  const financialCountry = financial?.Country as COUNTRY_SHORTS
  const financialCreditsApplied = financial?.CreditsApplied ?? 0
  const financialCreditsToBeApplied = financial?.CreditsToBeApplied ?? 0
  const financialCreditsToBeAppliedLater = financial?.CreditsToBeAppliedLater ?? 0
  const financialDeductibleAmount = financial?.DeductibleAmount ?? 0
  const financialDeductibleAmountBasic = financial?.DeductibleAmountBasic ?? 0
  const financialDeductibleAmountPremium = financial?.DeductibleAmountPremium ?? 0
  const financialDeductibleAmountStandard = financial?.DeductibleAmountStandard ?? 0
  const financialDeductibleAmountStationary = financial?.DeductibleAmountStationary ?? 0
  const financialDeductibleAmountStationaryPlus = financial?.DeductibleAmountStationaryPlus ?? 0
  const financialDepositAmount = financial?.DepositAmount ?? 0
  const financialDiscount = financial?.Discount ?? 0
  const financialDiscountsMidweekdays = financial?.Discounts?.Midweekdays
  const financialDiscountsMonthly = financial?.Discounts?.Monthly
  const financialDiscountsPayInFull = financial?.Discounts?.PayInFull
  const financialDiscountsTotalDiscount = financial?.Discounts?.TotalDiscount
  const financialDiscountsWeekly = financial?.Discounts?.Weekly
  const financialInsuranceProtectionLevel = financial?.InsuranceProtectionLevel ?? undefined
  const financialNightlyBase = financial?.Nightly?.Base ?? 0
  const financialNightlyDiscounted = financial?.Nightly?.Discounted ?? 0
  const financialNightlyInsuranceDiscountRateStationary = financial?.NightlyInsuranceDiscountRateStationary ?? 0
  const financialNightlyInsuranceRateBasic = financial?.NightlyInsuranceRateBasic ?? 0
  const financialNightlyInsuranceRatePremium = financial?.NightlyInsuranceRatePremium ?? 0
  const financialNightlyInsuranceRateStandard = financial?.NightlyInsuranceRateStandard ?? 0
  const financialNightlyInsuranceRateStationary = financial?.NightlyInsuranceRateStationary ?? 0
  const financialNightlyInsuranceRateStationaryPlus = financial?.NightlyInsuranceRateStationaryPlus ?? 0
  const financialNightlyRoadsideBasicRate = financial?.NightlyRoadsideBasicRate ?? 0
  const financialNightlyRoadsidePremiumRate = financial?.NightlyRoadsidePremiumRate ?? 0
  const financialNightlyRoadsideStandardRate = financial?.NightlyRoadsideStandardRate ?? 0
  const financialNightlyRoadsideStationaryPlusRate = financial?.NightlyRoadsideStationaryPlusRate ?? 0
  const financialNightlyRoadsideStationaryRate = financial?.NightlyRoadsideStationaryRate ?? 0
  const financialOwnerPayoutTotal = financial?.OwnerPayoutTotal ?? 0
  const financialOwnerReferralCredits = financial?.OwnerReferralCredits ?? 0
  const financialOwnerSalesTaxAmounts = financial?.OwnerSalesTaxAmounts ?? []
  const financialOwnerTaxAmounts = financial?.OwnerTaxAmounts ?? []
  const financialOwnerTotal = financial?.OwnerTotal ?? 0
  const financialPayInFullAmount = financial?.PayInFullAmount ?? 0
  const financialPayLater = financial?.PayLater ?? 0
  const financialPayLaterOn = financial?.PayLaterOn
  const financialPaymentMethodAddedOn = financial?.PaymentMethodAddedOn ?? undefined
  const financialPayments = financial?.Payments ?? []
  const financialPayToReserve = financial?.PayToReserve ?? 0
  const financialPendingRenterRefund = financial?.PendingRenterRefund ?? false
  const financialPromoCode = financial?.PromoCode ?? null
  const financialRenterDiscountedTotal = financial?.RenterDiscountedTotal ?? 0
  const financialRenterOutstandingBalance = financial?.RenterOutstandingBalance ?? 0
  const financialRenterTaxAmounts = financial?.RenterTaxAmounts ?? []
  const financialRenterTotal = financial?.RenterTotal ?? 0
  const financialSecurityDepositAmount = financial?.SecurityDepositAmount ?? 0
  const financialSecurityDepositBalance = financial?.SecurityDepositBalance ?? 0
  const financialSecurityDepositFailed = financial?.SecurityDepositFailed ?? undefined
  const financialSecurityDepositOn = financial?.SecurityDepositOn ?? undefined
  const financialSecurityDepositTaken = financial?.SecurityDepositTaken ?? undefined
  const financialStatus = financial?.Status
  const financialTotalAddOns = financial?.TotalAddOns ?? 0
  const financialTotalCleanings = financial?.TotalCleanings ?? 0
  const financialTotalCloseouts = financial?.TotalCloseouts ?? 0
  const financialTotalCreditsToBeApplied = financial?.TotalCreditsToBeApplied ?? 0
  const financialTotalDamages = financial?.TotalDamages ?? 0
  const financialTotalDelivery = financial?.TotalDelivery ?? 0
  const financialTotalDeliveryFee = financial?.TotalDeliveryFee ?? 0
  const financialTotalIncomeProtection = financial?.TotalIncomeProtection ?? 0
  const financialTotalIncomeProtectionPayout = financial?.TotalIncomeProtectionPayout ?? 0
  const financialTotalInsurances = financial?.TotalInsurances ?? 0
  const financialTotalNights = financial?.TotalNights ?? 0
  const financialTotalOwnerSalesTaxes = financial?.TotalOwnerSalesTaxes ?? 0
  const financialTotalOwnerServiceFee = financial?.TotalOwnerServiceFee ?? 0
  const financialTotalOwnerServiceFeeWithoutDelivery = financial?.TotalOwnerServiceFeeWithoutDelivery ?? 0
  const financialTotalOwnerTaxes = financial?.TotalOwnerTaxes ?? 0
  const financialTotalPaid = financial?.TotalPaid ?? 0
  const financialTotalPayoutAmounts = financial?.TotalPayoutAmounts ?? 0
  const financialTotalPreDiscountRentalAmount = financial?.TotalPreDiscountRentalAmount ?? 0
  const financialTotalPromotionalDiscountAmount = financial?.TotalPromotionalDiscountAmount ?? 0
  const financialTotalRentalAmount = financial?.TotalRentalAmount ?? 0
  const financialTotalRentalPaid = financial?.TotalRentalPaid ?? 0
  const financialTotalRenterServiceFee = financial?.TotalRenterServiceFee ?? 0
  const financialTotalRenterTaxes = financial?.TotalRenterTaxes ?? 0
  const financialTotalReversiblePayoutAmount = financial?.TotalReversiblePayoutAmount ?? 0
  const financialTotalRoadsides = financial?.TotalRoadsides ?? 0
  const financialTotalSecurityDepositPaymentReductions = financial?.TotalSecurityDepositPaymentReductions ?? 0
  const financialTotalSecurityDepositPayments = financial?.TotalSecurityDepositPayments ?? 0
  const financialTotalSecurityDeposits = financial?.TotalSecurityDeposits ?? 0
  const financialTotalUSTaxes = financial?.TotalUSTaxes ?? 0
  const financialUSTaxAmounts = financial?.USTaxAmounts ?? []

  // Advanced properties.
  const financialCurrency = lookupCountryCodeToCurrencyCode(financialCountry)
  const financialDiscountPercentage = financialDiscountsTotalDiscount?.Percentage ?? 0
  const financialDiscountsPayInFullDiscountAmount = financialDiscountsPayInFull?.DiscountAmount ?? 0
  const financialDiscountsPayInFullNumberOfNights = financialDiscountsPayInFull?.NumberOfNights ?? 0
  const financialDiscountsPayInFullPercentage = financialDiscountsPayInFull?.Percentage ?? 0
  const financialRvAddOns = financialAddOns.filter((addon) => addon.AddOnType === 'RVAddOn')
  const financialRvezyAmount = Number(((financialTotalRentalPaid) * 1.1).toFixed(2))
  const financialStationaryDiscountPercentage = decimalToPercentage(financialNightlyInsuranceDiscountRateStationary)
  const financialTotalDays = financialTotalNights + 1
  const financialTotalProtection = financialTotalInsurances + financialTotalRoadsides

  // Basic `is` checks.
  const financialHasStationaryDiscount = financialNightlyInsuranceDiscountRateStationary > 0
  const financialIsAwaitingOwnerApproval = financialStatus === 'AwaitingOwnerApproval'

  return {
    financialAddOns,
    financialAmountDue,
    financialBookingId,
    financialCountry,
    financialCreditsApplied,
    financialCreditsToBeApplied,
    financialCreditsToBeAppliedLater,
    financialDeductibleAmount,
    financialDeductibleAmountBasic,
    financialDeductibleAmountPremium,
    financialDeductibleAmountStandard,
    financialDeductibleAmountStationary,
    financialDeductibleAmountStationaryPlus,
    financialDepositAmount,
    financialDiscount,
    financialDiscountsMidweekdays,
    financialDiscountsMonthly,
    financialDiscountsPayInFull,
    financialDiscountsTotalDiscount,
    financialDiscountsWeekly,
    financialInsuranceProtectionLevel,
    financialNightlyBase,
    financialNightlyDiscounted,
    financialNightlyInsuranceDiscountRateStationary,
    financialNightlyInsuranceRateBasic,
    financialNightlyInsuranceRatePremium,
    financialNightlyInsuranceRateStandard,
    financialNightlyInsuranceRateStationary,
    financialNightlyInsuranceRateStationaryPlus,
    financialNightlyRoadsideBasicRate,
    financialNightlyRoadsidePremiumRate,
    financialNightlyRoadsideStandardRate,
    financialNightlyRoadsideStationaryPlusRate,
    financialNightlyRoadsideStationaryRate,
    financialOwnerPayoutTotal,
    financialOwnerReferralCredits,
    financialOwnerSalesTaxAmounts,
    financialOwnerTaxAmounts,
    financialOwnerTotal,
    financialPayInFullAmount,
    financialPayLater,
    financialPayLaterOn,
    financialPaymentMethodAddedOn,
    financialPayments,
    financialPayToReserve,
    financialPendingRenterRefund,
    financialPromoCode,
    financialRenterDiscountedTotal,
    financialRenterOutstandingBalance,
    financialRenterTaxAmounts,
    financialRenterTotal,
    financialSecurityDepositAmount,
    financialSecurityDepositBalance,
    financialSecurityDepositFailed,
    financialSecurityDepositOn,
    financialSecurityDepositTaken,
    financialStatus,
    financialTotalAddOns,
    financialTotalCleanings,
    financialTotalCloseouts,
    financialTotalCreditsToBeApplied,
    financialTotalDamages,
    financialTotalDelivery,
    financialTotalDeliveryFee,
    financialTotalIncomeProtection,
    financialTotalIncomeProtectionPayout,
    financialTotalInsurances,
    financialTotalNights,
    financialTotalOwnerSalesTaxes,
    financialTotalOwnerServiceFee,
    financialTotalOwnerServiceFeeWithoutDelivery,
    financialTotalOwnerTaxes,
    financialTotalPaid,
    financialTotalPayoutAmounts,
    financialTotalPreDiscountRentalAmount,
    financialTotalPromotionalDiscountAmount,
    financialTotalRentalAmount,
    financialTotalRentalPaid,
    financialTotalRenterServiceFee,
    financialTotalRenterTaxes,
    financialTotalReversiblePayoutAmount,
    financialTotalRoadsides,
    financialTotalSecurityDepositPaymentReductions,
    financialTotalSecurityDepositPayments,
    financialTotalSecurityDeposits,
    financialTotalUSTaxes,
    financialUSTaxAmounts,

    financialCurrency,
    financialDiscountPercentage,
    financialDiscountsPayInFullDiscountAmount,
    financialDiscountsPayInFullNumberOfNights,
    financialDiscountsPayInFullPercentage,
    financialRvAddOns,
    financialRvezyAmount,
    financialStationaryDiscountPercentage,
    financialTotalDays,
    financialTotalProtection,

    financialHasStationaryDiscount,
    financialIsAwaitingOwnerApproval,
  }
}
